/**
 * IMBEE-8954 — the campaign feature's diagnostic channel.
 *
 * REQ-CWC-04 requires no VISITOR-FACING error UI. It does not require no signal, and the two are
 * not the same thing: without this, a wrong wire field, an unreachable gateway and a widget that
 * never received a page context all produce exactly the observable of "this tenant has no matching
 * campaign", so a whole feature can ship dark and nothing a human looks at is different.
 *
 * WHY THE CONSOLE, AND NOT THE ORG'S SENTRY. A shared reporter does exist and was looked for
 * before writing this: the sibling frontend `imbee-cms` declares `@sentry/react` and
 * `@sentry/tracing` (its `package.json`; both resolve to 6.19.7 in its installed tree) against
 * imBee's self-hosted Sentry. `web-chat` declares neither — `package.json` here has no `sentry`
 * entry at all.
 *
 * It is declined on ONE reason, and that reason is a measured number: payload. This bundle is
 * served into an iframe embedded on arbitrary third-party customer pages, so every byte is a cost
 * imBee imposes on every page view of every customer's site — not on an imBee-owned page that a
 * logged-in operator chose to open, which is the situation `imbee-cms` is in. Measured 2026-08-29
 * against the 6.19.7 tree `imbee-cms` already installs, the SDK's own browser bundle
 * (`node_modules/@sentry/browser/build/bundle.es6.min.js`) is 60,059 bytes minified and 19,592
 * bytes gzipped, before `@sentry/react`'s wrapper and before any transport configuration. Nothing
 * else is claimed against Sentry here; this one cost carries the decision by itself.
 *
 * What declining costs, stated rather than buried: console-only means NO signal ever reaches
 * imBee. Every failure named below is visible only to whoever happens to have DevTools open on
 * that tenant's site at that moment, so a tenant's campaigns can be dark for a week and nobody at
 * imBee finds out. For a feature whose whole failure mode is silence that is a real limitation,
 * not a footnote — which is why the trade-off is routed to the TDS (§6c) as a doc-change rather
 * than left implied by what this module happens to do. The design is where it should be overturned
 * if the balance is judged differently; this file does not get to settle it.
 *
 * ⚠ The console these lines land in is NOT the loader's. `loader/web-chat-loader.js` runs as a
 * top-level script in the HOST page; this module is bundled into the React app that runs INSIDE
 * the iframe, on a different origin. In DevTools the two are separate frame contexts and their
 * lines never interleave, so a developer must select the widget's frame to see these. The
 * distinct `[imbee-campaign]` prefix (vs the loader's `[ImbeeWebChat]`) is what makes it obvious
 * which side of the boundary produced a line.
 *
 * Every message carries the specific field or call that failed, never just "something went wrong".
 */
const PREFIX = '[imbee-campaign]';

/** A failure that means this visitor will see no campaign when they arguably should have. */
export const campaignWarn = (event: string, detail?: Record<string, unknown>): void => {
  if (typeof console === 'undefined' || !console.warn) return;
  console.warn(PREFIX, event, detail ?? {});
};

/** Ordinary lifecycle — noisy by design, off unless the console's verbose level is on. */
export const campaignDebug = (event: string, detail?: Record<string, unknown>): void => {
  if (typeof console === 'undefined' || !console.debug) return;
  console.debug(PREFIX, event, detail ?? {});
};
